/**
 * `chat` namespace — English canonical source.
 *
 * The AI strategy builder: the launcher (routes/chat/index.tsx), the two-panel
 * session (routes/chat/$sessionId.tsx), the streaming conversation
 * (features/chat/components/Conversation.tsx), the live DSL document
 * (features/chat/components/DslDocument.tsx), and the structured JSON editor
 * (features/chat/components/JsonTreeEditor.tsx). Covers page chrome, tool-activity
 * chips, validity/issue copy, the persist indicator, and the save-as-strategy bar.
 * Count-sensitive strings carry a singular/plural pair (English pluralizes; Korean
 * uses one form). The neutral Remove action reuses `common.remove`. Ticker symbols,
 * RSI, DSL, JSON, and AI stay English. `ko/chat.ts` mirrors this shape (a missing
 * key is a type error).
 */
export const chat = {
  // ── launcher chrome ──
  eyebrow: "AI builder",
  pageTitle: "Strategy chat",
  pageDescription:
    "Draft and refine strategies in conversation; the DSL document builds itself alongside.",
  sessionsHeading: "Sessions",
  sessionsLoadError: "Could not load sessions.",

  // ── new session ──
  startSessionHeading: "Start a session",
  startSessionDesc:
    "Build a new strategy from scratch, or refine an existing one with its live document and performance history in context.",
  titleLabel: "Title (optional)",
  titlePlaceholder: "e.g. RSI dip-buyer",
  baseLabel: "Base",
  startFresh: "Start fresh",
  refineOption: "Refine · {name}",
  starting: "Starting…",
  startBuilding: "Start building",
  startError: "Could not start a session.",

  // ── AI-locked notice ──
  aiNotConfigured: "AI is not configured",
  aiLockedDesc:
    "The strategy builder needs an OpenAI or Anthropic key. Add one in Settings → AI to unlock conversational strategy building. You can still browse past sessions below.",
  configureAi: "Configure AI",

  // ── session list ──
  untitledSession: "Untitled session",
  refining: "Refining",
  hasDraft: "Has draft",
  updated: "Updated {date}",
  sessionsEmptyTitle: "No sessions yet",
  sessionsEmptyDesc: "Start one above to build your first strategy in chat.",
  deleteSession: "Delete",
  deleteConfirm: "Really delete?",
  deleting: "Deleting…",
  deleteError: "Could not delete the session. Please try again.",

  // ── session page ──
  loadingSession: "Loading session…",
  sessionLoadError: "This session could not be loaded.",
  backToSessions: "Back to sessions",
  builderTitle: "Strategy builder",

  // ── conversation: reasoning + tool chips ──
  reasoning: "Reasoning",
  reasoningStreaming: "Reasoning…",
  toolWriting: "Writing the document…",
  toolWrote: "Rewrote the document",
  toolEditing: "Editing the document…",
  toolEdited: "Edited the document",
  changeOne: "{count} change",
  changeMany: "{count} changes",
  toolReadStats: "Read performance history",
  toolReadBacktest: "Read backtest results",
  toolSearching: "Searching the web…",
  toolSearched: "Searched the web",
  toolFetching: "Reading page…",
  toolFetched: "Read page",
  toolFindingTools: "Finding a tool…",
  toolFoundTools: "Searched for a tool",
  statsTradesOne: "{trades} trade",
  statsTradesMany: "{trades} trades",
  statsWinRateSuffix: ", {pct}% win rate",

  // ── conversation: expanded tool detail ──
  editSet: "Set",
  editValid: "Valid",
  editMissing: "{count} missing",
  editErrors: "{count} errors",
  searchNoResults: "No results",
  searchFailed: "Search failed",
  toolSearchNoMatch: "No matching tool",
  toolSearchLoaded: "Loaded: {names}",
  fetchEmpty: "No readable content",
  fetchFailed: "Could not read the page",
  fetchTruncated: "Truncated",
  detailInput: "Input",
  detailOutput: "Output",
  toolFailed: "Failed",

  // ── conversation: ambient + errors ──
  thinking: "Thinking…",
  resumingTurn: "Resuming the running turn…",
  messageRenderError: "This message could not be displayed.",
  introTitle: "Describe the strategy you want",
  introExample:
    "e.g. “Buy KODEX 200 when its 14-day RSI drops below 30, sized at 20% of budget, and notify me — dry-run only.” The document appears on the right as it takes shape.",
  errorLocked: "AI is not configured — add a provider key in Settings → AI to build strategies.",
  errorRefusal: "The model declined this request; the document was left untouched. Try rephrasing.",
  errorTurnFailed: "The turn failed. Please try again.",

  // ── composer ──
  composerPlaceholder: "Message the strategy builder…",
  stop: "Stop",
  send: "Send",

  // ── document header ──
  docTitle: "Strategy document",
  noDocument: "No document yet",
  untitledStrategy: "Untitled strategy",
  badgeEmpty: "Empty",
  badgeValid: "Valid DSL",
  issueOne: "{count} issue",
  issueMany: "{count} issues",
  modeStructured: "structured",
  modeJson: "json",
  syncFailed: "Sync failed",
  savingDraft: "Saving draft…",
  draftSynced: "Draft synced",

  // ── validity issues ──
  issuesTitle: "This document is not yet a valid strategy",
  issuesMore: "+{count} more…",
  errorNotObject: "The document must be a JSON object.",
  errorInvalidJson: "Invalid JSON",

  // ── empty document ──
  emptyDocTitle: "The document builds itself here",
  emptyDocDesc:
    "Describe the strategy on the left. As the assistant drafts it, the DSL streams in and every field becomes editable.",

  // ── save as strategy ──
  saveNamePlaceholder: "Strategy name (optional)",
  saving: "Saving…",
  saveAsStrategy: "Save as strategy",
  saveHintCount:
    "Resolve {count} validation issues before saving — new strategies always start as a dry-run draft.",
  saveHintOutstanding:
    "Resolve the outstanding validation issues before saving — new strategies always start as a dry-run draft.",
  saveError: "Could not save the strategy.",

  // ── json tree editor ──
  jsonEmpty: "empty",
  jsonAddField: "add field",
  jsonFieldName: "field name",
} as const;

export type ChatKey = keyof typeof chat;
